import * as React from 'react';
import styled, { css } from 'styled-components';
import AspectRatio from '../Layout/AspectRatio';
import { IDSContentfulVideoFile } from '@ids/types/contentful';

const StyledVideo = styled.video<{ $backgroundImage?: string; $videoLoaded: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: cover;

    ${({ $backgroundImage, $videoLoaded }) =>
        $backgroundImage &&
        !$videoLoaded &&
        css`
            background: url(${$backgroundImage}) center center / cover no-repeat;
        `}
`;

/**
 * This hook is used to avoid an issue on iOS devices when the video is set to
 * autoplay and it has a poster image.
 *
 * The issue is that there is a flash before the video starts playing and
 * right after the poster has been removed.
 */
export const useVideoPoster = (autoPlay: boolean, posterUrl?: string) => {
    if (!posterUrl) {
        return undefined;
    }

    if (autoPlay) {
        return {
            $backgroundImage: posterUrl,
        };
    }

    return {
        poster: posterUrl,
    };
};

interface MediaVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
    /**
     * Accepts a boolean value.
     * If true it will preload the video component.
     *
     * @default false
     */
    priority?: boolean;
    ratio?: number;
    video: IDSContentfulVideoFile;
}

const MediaVideo = ({
    video: { autoPlay = true, loop = true, controls = false, muted = true, videoFile, poster },
    priority,
    ...videoProps
}: MediaVideoProps) => {
    const [videoLoaded, setVideoLoaded] = React.useState(false);
    const videoPoster = useVideoPoster(autoPlay, poster?.file.url ?? videoProps.poster);

    return (
        <StyledVideo
            autoPlay={autoPlay ?? videoProps.autoPlay}
            loop={loop ?? videoProps.loop}
            controls={controls ?? videoProps.controls}
            preload={priority ? 'auto' : 'metadata'}
            muted={muted ?? videoProps.muted}
            title={videoFile.title ?? videoProps.title}
            playsInline
            onPlay={() => setTimeout(() => setVideoLoaded(true), 300)}
            $videoLoaded={videoLoaded}
            {...videoPoster}
            {...videoProps}
        >
            <source key={videoFile.title} src={videoFile.file.url} type={videoFile.file.contentType} />
        </StyledVideo>
    );
};

interface MediaVideoWithLayoutProps extends MediaVideoProps {
    ratio?: number;
}

const MediaVideoWithLayout = ({ video, priority, ratio, ...videoProps }: MediaVideoWithLayoutProps) => {
    if (!video.videoFile) {
        return null;
    }

    return ratio ? (
        <AspectRatio ratio={ratio}>
            <MediaVideo priority={priority} video={video} {...videoProps} />
        </AspectRatio>
    ) : (
        <MediaVideo priority={priority} video={video} {...videoProps} />
    );
};

MediaVideo.Full = MediaVideoWithLayout;

export { MediaVideoWithLayout as default, MediaVideo };
